import { useTranslation } from 'react-i18next';
import { Card, CardContent, TooltipProvider } from '@monksflow/monks-ui';
import { totalSpendOf, type ProjectPerformance } from '@/entities/media-campaign';
import { formatMetric } from '@/shared/utils/metric-format';
import { deriveRates } from '../../lib/metrics';
import { MetricTile } from '../MetricTile';
import { TrendCharts, type TrendMetric } from '../TrendCharts';
import { BreakdownTable } from '../BreakdownTable';
import { ConversionFunnel } from '../ConversionFunnel';
import { SpendShare } from '../SpendShare';

export interface PerformanceDashboardProps {
  performance: ProjectPerformance | undefined;
  previous?: ProjectPerformance | undefined;
  trendMetric: TrendMetric;
  onTrendMetricChange: (metric: TrendMetric) => void;
  isLoading: boolean;
}

/**
 * Composes the tile row, trend chart, funnel, spend share and breakdown table into the
 * Performance tab's Overview.
 *
 * Mounts one `TooltipProvider` around the tile row. `MetricTile` deliberately does not
 * self-mount one (see its JSDoc) and the app has no ambient provider at its root, so a
 * tile that goes unreported - an ad-server-only project's spend, for one - would
 * otherwise throw when it renders its em-dash Tooltip. The funnel and share bar mount no
 * tooltip of their own, so they stay outside that provider rather than widening its
 * scope for no reason.
 */
export function PerformanceDashboard({
  performance,
  previous,
  trendMetric,
  onTrendMetricChange,
  isLoading,
}: PerformanceDashboardProps) {
  const { t } = useTranslation('projects');
  const spend = performance ? totalSpendOf(performance) : null;
  const previousSpend = previous ? totalSpendOf(previous) : null;
  const currency = spend?.currency ?? null;

  const totals = {
    spend: spend?.amount ?? null,
    impressions: performance?.impressions ?? null,
    clicks: performance?.clicks ?? null,
    conversions: performance?.conversions ?? null,
  };
  const rates = deriveRates(totals);

  const isMixedCurrency =
    performance !== undefined &&
    !performance.isSingleCurrency &&
    (performance.spendByCurrency.length > 1 || performance.unknownCurrencyCampaignCount > 0);

  // One line per currency that reported one. Spend whose currency the platform never
  // supplied is left out rather than printed bare: a figure with no currency beside
  // three that have one invites being read in whichever currency is nearest.
  const spendLines = isMixedCurrency
    ? performance.spendByCurrency.map((entry) =>
        formatMetric(entry.spend, 'currency', entry.currency),
      )
    : undefined;

  // Distinct from isMixedCurrency: an ad-server-only project (nothing reports spend at
  // all) satisfies neither of that condition's branches, so without this it would fall
  // through to a bare, unlabelled dash instead of its own reason.
  const hasNoSpendReported =
    performance !== undefined &&
    performance.spendByCurrency.length === 0 &&
    performance.unknownCurrencyCampaignCount === 0;

  // Spend exists but no currency was reported for any of it, so there is nothing to
  // label and nothing to list. Without its own reason this would reach the tile as a
  // bare dash, which reads as a failure to load rather than as unlabelled money.
  const hasOnlyUnlabelledSpend =
    performance !== undefined &&
    performance.spendByCurrency.length === 0 &&
    performance.unknownCurrencyCampaignCount > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="performance-overview-dashboard">
      <TooltipProvider>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.spend')}
            value={spend?.amount ?? null}
            previousValue={previousSpend?.amount ?? null}
            format="currency"
            currency={currency}
            valueLines={spendLines}
            unreportedReason={
              hasNoSpendReported
                ? t('performanceDashboard.performanceDashboard.noSpendReported')
                : hasOnlyUnlabelledSpend
                  ? t('performanceDashboard.performanceDashboard.noCurrencyReported')
                  : undefined
            }
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.impressions')}
            value={performance?.impressions ?? null}
            previousValue={previous?.impressions ?? null}
            format="integer"
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.clicks')}
            value={performance?.clicks ?? null}
            previousValue={previous?.clicks ?? null}
            format="integer"
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.ctr')}
            value={rates.ctr}
            format="percent"
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.conversions')}
            value={performance?.conversions ?? null}
            previousValue={previous?.conversions ?? null}
            format="integer"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.cpm')}
            value={rates.cpm}
            format="currency"
            currency={currency}
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.cpc')}
            value={rates.cpc}
            format="currency"
            currency={currency}
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.cpa')}
            value={rates.cpa}
            format="currency"
            currency={currency}
          />
          <MetricTile
            label={t('performanceDashboard.performanceDashboard.cvr')}
            value={rates.cvr}
            format="percent"
          />
        </div>
      </TooltipProvider>

      <Card compact>
        <CardContent compact>
          <TrendCharts
            data={performance?.metrics ?? []}
            metric={trendMetric}
            onMetricChange={onTrendMetricChange}
            currency={currency}
            // Defaults to true while there is no response yet, so the chart renders its own
            // loading state rather than the mixed-currency notice.
            isSingleCurrency={performance?.isSingleCurrency ?? true}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card compact>
          <CardContent compact>
            <ConversionFunnel totals={totals} isLoading={isLoading} />
          </CardContent>
        </Card>

        <Card compact>
          <CardContent compact>
            <SpendShare rows={performance?.breakdown ?? []} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>

      <Card compact>
        <CardContent compact>
          <BreakdownTable
            rows={performance?.breakdown ?? []}
            dimension={performance?.dimension ?? 'platform'}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
